import { useMemo, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type LANG } from "@/models";
import { CHLD } from "@/models/chld";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { FiPlus } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import LANGEditDialog from "./dialogs/LANGEditDialog";
import { buildLangAddDefaults } from "./useLANGFormLogic";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  {
    id: "EffectiveStartDate",
    field: "EffectiveStartDate",
    title: "Effective Start Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Effective Start Date", key: "EffectiveStartDate" },
];

type LangRow = LANG & {
  PreschoolName?: string;
  ChildName?: string;
  __raw: LANG;
};

function getMostRecentLang(records: LANG[]): LANG | undefined {
  if (records.length === 0) return undefined;
  return [...records].sort((a, b) => {
    const da = new Date(a.EffectiveStartDate ?? "").getTime();
    const db = new Date(b.EffectiveStartDate ?? "").getTime();
    return (
      (Number.isNaN(db) ? -Infinity : db) - (Number.isNaN(da) ? -Infinity : da)
    );
  })[0];
}

export default function LANGRecordsTab({
  langRecords,
  child,
}: {
  langRecords: LANG[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [popupLang, setPopupLang] = useState<LANG | undefined>(undefined);
  const [langDefaults, setLangDefaults] = useState<Partial<LANG> | undefined>(
    undefined
  );

  const canEdit = leaContext.hasRole("LANGEdit");

  const closeDialog = () => {
    setDialogOpen(false);
    setPopupLang(undefined);
    setLangDefaults(undefined);
  };

  const onAddButtonClick = () => {
    const mostRecent = getMostRecentLang(langRecords);
    const defaults: Partial<LANG> = mostRecent
      ? buildLangAddDefaults(mostRecent)
      : {
          LocalChildId: child.LocalChildId ?? "",
          PreschoolCode: child.PreschoolCode ?? "",
          ProgramYear: child.ProgramYear ?? "",
          CSPPID: child.CSPPID ?? "",
        };
    setLangDefaults(defaults);
    setPopupLang(undefined);
    setDialogOpen(true);
  };

  const onEditButtonClick = (record: LANG) => {
    setLangDefaults(undefined);
    setPopupLang(record);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData: LangRow[] = useMemo(() => {
    return (langRecords ?? []).map((record) => ({
      ...record,
      ChildName: childName,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      EffectiveStartDate: record.EffectiveStartDate
        ? formatDateOnly(record.EffectiveStartDate)
        : "",
      __raw: record,
    }));
  }, [langRecords, leaContext?.currentOrgPreschools, childName]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export language records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["ChildName"], "language-record")}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add a language record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) =>
          onEditButtonClick((event.dataItem as LangRow).__raw)
        }
      />

      {dialogOpen && (
        <LANGEditDialog
          selectedLang={popupLang}
          langDefaults={langDefaults}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
